import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

export function CopyButton({ value, className, label = "Copy" }: { value: string; className?: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      aria-label={label}
      className={cn("inline-flex cursor-pointer items-center rounded p-1 text-fg-3 hover:bg-surface-3 hover:text-fg", className)}
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard.writeText(value).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        });
      }}
    >
      {done ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </button>
  );
}

export function CodeBlock({ code, language, className, maxHeight = 360 }: { code: string; language?: string; className?: string; maxHeight?: number }) {
  return (
    <div className={cn("group relative rounded-md border bg-surface-2", className)}>
      <div className="absolute right-1.5 top-1.5 flex items-center gap-1 opacity-60 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        {language ? <span className="font-mono text-[10px] uppercase text-fg-3">{language}</span> : null}
        <CopyButton value={code} />
      </div>
      <pre className="scrollbar-thin overflow-auto p-3 font-mono text-[12px] leading-relaxed text-fg-2" style={{ maxHeight }}>
        <code>{code}</code>
      </pre>
    </div>
  );
}

/** Renders unified-diff-ish text with +/- line coloring. */
export function DiffView({ diff, className }: { diff: string; className?: string }) {
  return (
    <div className={cn("group relative rounded-md border bg-surface-2", className)}>
      <div className="absolute right-1.5 top-1.5 opacity-0 transition-opacity group-hover:opacity-100">
        <CopyButton value={diff} />
      </div>
      <pre className="scrollbar-thin overflow-auto py-2 font-mono text-[12px] leading-relaxed">
        {diff.split("\n").map((line, i) => {
          const add = line.startsWith("+") && !line.startsWith("+++");
          const del = line.startsWith("-") && !line.startsWith("---");
          const hunk = line.startsWith("@@") || line.startsWith("---") || line.startsWith("+++");
          return (
            <div key={i} className={cn("px-3", add && "bg-good/10 text-good-text", del && "bg-critical/10 text-critical-text", hunk && "text-fg-3", !add && !del && !hunk && "text-fg-2")}>
              {line || " "}
            </div>
          );
        })}
      </pre>
    </div>
  );
}

export function Hash({ value, n = 10, className }: { value: string; n?: number; className?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-0.5 font-mono text-[12px] text-fg-2", className)} title={value}>
      {value.length > n ? `${value.slice(0, n)}…` : value}
      <CopyButton value={value} />
    </span>
  );
}
